import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { BuyXGetY, BuyXGetYReqBody, PageType } from "@/types/coupon/discount";
import React, { useState } from "react";
import SaveDiscountCouponSection from "../saveDiscountCouponSection";
import { createDiscount } from "@/services/discountService";
import { resetBuyXGetY } from "@/store/slices/discount.slice";
import { toast } from "sonner";
import { useNavigate, useParams } from "react-router-dom";
import { convertCurrencyToDecimal } from "@/utils/currency";

// UUID validation utility
const isValidUUID = (id?: string) =>
  typeof id === "string" &&
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(
    id,
  );

const SaveBuyXGetY = ({ currentPage }: { currentPage: PageType }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { storeType, storeId } = useParams<{ storeType: string; storeId: string }>();
  const [loading, setLoading] = useState(false);
  
  const { couponId, ...rest } = useAppSelector(
    (state) => state.discount[currentPage],
  ) as BuyXGetY & { couponId?: string };
  // Destructure all required fields from rest
  const {
    title,
    discount_code,
    discount_type,
    discount_value_percentage,
    discount_value_fixed,
    end_date,
    minimum_purchase_type,
    start_date,
    applicable_x_products,
    applicable_x_collections,
    applicable_y_products,
    applicable_y_collections,
    minimum_purchase_value_amount,
    minimum_purchase_value_quantity,
    discount_value_max,
    get_y_quantity,
  } = rest;
  const currentTab = useAppSelector((state) => state.discount.currentTab);

  const handleSave = async () => {
    // Edge case: Ensure title and discountCode are not empty
    const adjustedTitle = title || discount_code;
    const adjustedDiscountCode = discount_code || title;

    const buyXProducts = applicable_x_products.map((product) => product.id);

    const buyXVariants = applicable_x_products.flatMap((product) =>
      product.variants.map((variant) => variant.id),
    );

    const getYProducts = applicable_y_products.map((product) => product.id);

    const getYVariants = applicable_y_products.flatMap((product) =>
      product.variants.map((variant) => variant.id),
    );

    // If discount_type is 'free', convert to percentage and value 100 in payload
    const isFree = discount_type === "free";
    const validCouponId = isValidUUID(couponId) ? couponId : undefined;
    const reqBody: BuyXGetYReqBody = {
      discountType: isFree ? "percentage" : discount_type,
      enabled: false,
      getYquantity: Number(get_y_quantity),
      automatic: currentTab === "automatic",
      buyXCollection: applicable_x_collections.map(
        (collection) => collection.id,
      ),
      buyXProducts,
      buyXVariants,
      discountCode: adjustedDiscountCode,
      discountValue: isFree
        ? 100
        : Number(
            discount_type === "percentage"
              ? discount_value_percentage
              : convertCurrencyToDecimal(Number(discount_value_fixed)),
          ),
      endDate: end_date ? new Date(end_date).getTime() : null,
      getYCollection: applicable_y_collections.map(
        (collection) => collection.id,
      ),
      getYProducts,
      getYVariants,
      minimumPurchaseType: minimum_purchase_type,
      minimumPurchaseValue: Number(
        minimum_purchase_type === "amount"
          ? convertCurrencyToDecimal(Number(minimum_purchase_value_amount))
          : minimum_purchase_type === "quantity"
            ? minimum_purchase_value_quantity
            : 0,
      ),
      startDate: start_date ? new Date(start_date).getTime() : null,
      title: adjustedTitle,
      ...(Number(discount_value_max) > 0 && {
        discountValueMax: convertCurrencyToDecimal(Number(discount_value_max)),
      }),
      ...(validCouponId && { couponId: validCouponId }),
    };

    if (reqBody.automatic) {
      delete reqBody.discountCode;
    }

    const endpoint = validCouponId
      ? `buy-x-get-y?id=${validCouponId}`
      : `buy-x-get-y`;
    // Log UUID and endpoint
    const vConsole = typeof window !== "undefined" && (window as any).vConsole;
    if (vConsole) {
      vConsole.log("[Coupon Save] UUID:", validCouponId);
      vConsole.log("[Coupon Save] Endpoint:", endpoint);
      vConsole.log("[Coupon Save] Request Body:", reqBody);
    } else {
      console.log("[Coupon Save] UUID:", validCouponId);
      console.log("[Coupon Save] Endpoint:", endpoint);
      console.log("[Coupon Save] Request Body:", reqBody);
    }

    try {
      setLoading(true);
      const response = await createDiscount({
        discountSlug: endpoint,
        data: reqBody,
      });
      if (vConsole) {
        vConsole.log("[Coupon Save] Response:", response);
      } else {
        console.log("[Coupon Save] Response:", response);
      }
      dispatch(resetBuyXGetY());
      toast.success("Coupon created successfully");
      navigate(`/${storeType}/${storeId}/coupons`);
    } catch (error) {
      console.error("Create discount failed:", error);
      toast.error("Failed to create coupon");
    } finally {
      setLoading(false);
    }
  };
  return <SaveDiscountCouponSection loading={loading} onSave={handleSave} />;
};

export default SaveBuyXGetY;
